/**
 * Firebase Storageと連携したlightbox機能
 */

import { storage } from '../firebase/config';
import { ref, getBlob } from "firebase/storage";

/**
 * 単一の要素に対してlightbox用の画像URLを準備
 * Firebase Storageから認証付きで取得してBlob URLに変換し、x-lightbox属性を更新
 */
async function prepareLightboxForElement(element) {
  const firebasePath = element.dataset.lightboxFirebasePath;
  const useDirectUrl = element.dataset.lightboxUseDirectUrl === 'true';
  if (!firebasePath) return;

  // 既に処理済みの場合はスキップ
  if (element.dataset.lightboxPrepared === 'true') return;
  element.dataset.lightboxPrepared = 'true';

  try {
    let imageUrl;

    if (useDirectUrl) {
      // 開発環境（エミュレータ）：直接URLを使用
      imageUrl = firebasePath;
    } else {
      // 本番環境：Firebase SDKで認証付き取得
      const storageRef = ref(storage, firebasePath);
      const blob = await getBlob(storageRef);
      const blobUrl = URL.createObjectURL(blob);
      imageUrl = blobUrl;

      // クリーンアップ用にBlob URLを保存
      element.dataset.lightboxBlobUrl = blobUrl;

      // ページ離脱時にBlob URLを解放
      window.addEventListener('beforeunload', () => {
        if (element.dataset.lightboxBlobUrl) {
          URL.revokeObjectURL(element.dataset.lightboxBlobUrl);
        }
      });
    }

    // x-lightbox属性を設定
    const group = element.dataset.lightboxGroup || 'default';
    const lightboxConfig = {
      url: imageUrl,
      group: group
    };

    element.setAttribute('x-lightbox', JSON.stringify(lightboxConfig));

  } catch (error) {
    console.error(`Lightbox用画像取得失敗:`, error);
  }
}

/**
 * lightbox用の画像URLを事前に準備
 * Firebase Storageから認証付きで取得してBlob URLに変換し、x-lightbox属性を更新
 */
async function prepareLightboxImages() {
  // img要素のみを対象（span要素は除外）
  const lightboxImages = document.querySelectorAll('img[data-lightbox-firebase-path][data-lightbox-prepared="false"]');

  for (const element of lightboxImages) {
    await prepareLightboxForElement(element);
  }
}

/**
 * lightboxのクリックイベントがモーダルに伝播しないようにする
 * 背景クリックでlightboxが閉じないようにする
 */
function setupLightboxEventStopPropagation(lightboxElement) {
  // 既に設定済みの場合はスキップ
  if (lightboxElement.dataset.lightboxStopPropagationSetup === 'true') {
    return;
  }
  lightboxElement.dataset.lightboxStopPropagationSetup = 'true';

  // lightbox要素全体のクリックイベントで伝播を停止
  lightboxElement.addEventListener('click', (event) => {
    // lightbox内のクリックはモーダルに伝播させない
    event.stopPropagation();
  }, false);

  // 背景クリック（画像以外）でlightboxが閉じないようにする
  const setupBackgroundClickHandler = () => {
    const listItems = lightboxElement.querySelectorAll('li[role="group"]');
    listItems.forEach((item) => {
      // 既に設定済みの場合はスキップ
      if (item.dataset.lightboxBackgroundHandlerSetup === 'true') {
        return;
      }
      item.dataset.lightboxBackgroundHandlerSetup = 'true';
      item.addEventListener('click', (event) => {
        // 画像自体をクリックした場合は何もしない
        if (event.target.tagName === 'IMG' || event.target.closest('img')) {
          return;
        }
        // 閉じるボタンやPrevious/Nextボタンをクリックした場合は何もしない
        const isButton = event.target.closest('a[aria-label="Close"], a[aria-label="Previous"], a[aria-label="Next"]');
        if (isButton) {
          return;
        }
        event.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();
      }, true);
    });

    // 画像数に応じて矢印を非表示にする
    const lightboxId = lightboxElement.id || '';
    const groupId = lightboxId.replace('lightbox-', '');

    // ページ内で同じグループIDを持つ画像要素の数をカウント
    const groupImages = document.querySelectorAll(`[data-lightbox-group="${groupId}"]`);
    const imageCount = groupImages.length;

    if (imageCount <= 1) {
      lightboxElement.setAttribute('data-single-image', 'true');
    } else {
      lightboxElement.removeAttribute('data-single-image');
    }
  };

  // 初回実行
  setTimeout(setupBackgroundClickHandler, 100);

  // li要素が追加されるのを監視
  const lightboxObserver = new MutationObserver(() => {
    setupBackgroundClickHandler();
  });
  lightboxObserver.observe(lightboxElement, {
    childList: true,
    subtree: true
  });

  // lightbox要素が削除されたら監視を停止
  const cleanupObserver = new MutationObserver(() => {
    if (!document.body.contains(lightboxElement)) {
      lightboxObserver.disconnect();
      cleanupObserver.disconnect();
    }
  });
  cleanupObserver.observe(document.body, { childList: true, subtree: true });
}

/**
 * 初期化処理
 */
function initLightboxFirebase() {
  // 既存のlightbox要素にもイベント伝播を防ぐ処理を追加
  document.querySelectorAll('[id^="lightbox-"]').forEach((lightbox) => {
    setupLightboxEventStopPropagation(lightbox);
  });

  // turbo streamの監視
  // ページ内のDOM変更を監視するためのMutationObserverを設定
  const observer = new MutationObserver(() => {
    // lightbox用画像の準備
    prepareLightboxImages();
    // lightbox要素が作成されたら、イベント伝播を防ぐイベントリスナーを追加
    document.querySelectorAll('[id^="lightbox-"]').forEach((lightbox) => {
      setupLightboxEventStopPropagation(lightbox);
    });
  });
  observer.observe(document.body, {
    childList: true,
    attributes: false,
    subtree: true
  });
}

// ページ読み込み時の処理
document.addEventListener('turbo:load', () => {
  initLightboxFirebase();
});

// エクスポート
export { prepareLightboxForElement, prepareLightboxImages, setupLightboxEventStopPropagation, initLightboxFirebase };

